/**
 * CatalogueTypeahead (NOVA-011/032) — the ONE shared catalogue picker both reception's BasketBuilder and
 * admin's PricingPage mount. It owns the debounced query + the accessible listbox (combobox pattern: roving
 * ArrowUp/Down, Enter to pick, Escape to close); the data hook + per-item render are injected by the caller
 * so the same widget serves the lab-overlay projection (parametre_labo_id + prix) and the central-code
 * projection (code + libellé). Gating is the CALLER's job (the screen passes its own cap to useCan);
 * the server re-checks `catalogue.parametre.read` on every request regardless.
 *
 * Generic over the row type T. The caller provides:
 *  - `useSearch(q, enabled)` — a TanStack query hook returning T[] (one of the catalogue api hooks).
 *  - `getKey` / `renderItem` — identity + display per row.
 *  - `onSelect` — what to do with a picked row (add a basket line / seed the price form).
 */
import { useId, useMemo, useRef, useState } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { Input, Spinner } from "@/ui";
import { useT } from "@/app";
import { token } from "@/design/theme";
import type { ApiError } from "@/api";
import { useDebounced } from "./useDebounced";

export interface CatalogueTypeaheadProps<T> {
  /** A catalogue search hook: (debouncedQuery, enabled) → query result of T[]. */
  useSearch: (q: string, enabled: boolean) => UseQueryResult<T[], ApiError>;
  /** Stable per-row key. */
  getKey: (row: T) => string;
  /** The visible row content (e.g. libellé + code + prix). */
  renderItem: (row: T) => React.ReactNode;
  /** Called with the picked row. The input is cleared afterwards. */
  onSelect: (row: T) => void;
  /** Disable the picker entirely (e.g. the caller lacks the gating capability). */
  disabled?: boolean;
  placeholder?: string;
  "aria-label"?: string;
  /** Render the field with the mono face (codes/matricules). */
  mono?: boolean;
  /** Minimum chars before the query fires (default 1). */
  minChars?: number;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  autoFocus?: boolean;
}

export function CatalogueTypeahead<T>({
  useSearch,
  getKey,
  renderItem,
  onSelect,
  disabled = false,
  placeholder,
  "aria-label": ariaLabel,
  mono = false,
  minChars = 1,
  inputMode,
  autoFocus,
}: CatalogueTypeaheadProps<T>) {
  const { t } = useT();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const debouncedQ = useDebounced(q, 250);
  const listId = useId();
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const ready = !disabled && debouncedQ.trim().length >= minChars;
  const query = useSearch(debouncedQ, ready);
  const rows = useMemo(() => query.data ?? [], [query.data]);

  const show = open && ready;

  const pick = (row: T) => {
    onSelect(row);
    setQ("");
    setActive(0);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!show || rows.length === 0) {
      if (e.key === "Escape") setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % rows.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + rows.length) % rows.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const row = rows[active];
      if (row) pick(row);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div style={{ position: "relative" }} role="combobox" aria-expanded={show} aria-haspopup="listbox" aria-owns={listId}>
      <Input
        type="search"
        mono={mono}
        autoFocus={autoFocus}
        disabled={disabled}
        value={q}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
        {...(inputMode ? { inputMode } : {})}
        onChange={(e) => {
          setQ(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        onBlur={() => {
          // Defer close so a mousedown on an option still registers.
          blurTimer.current = setTimeout(() => setOpen(false), 120);
        }}
      />

      {show && (
        <ul
          id={listId}
          role="listbox"
          style={{
            position: "absolute",
            insetInlineStart: 0,
            insetInlineEnd: 0,
            zIndex: 20,
            marginBlockStart: token.s1,
            maxHeight: 280,
            overflowY: "auto",
            listStyle: "none",
            padding: token.s1,
            margin: 0,
            background: token.surface,
            border: `1px solid ${token.lineSoft}`,
            borderRadius: token.rSm,
            boxShadow: token.e2,
          }}
        >
          {query.isLoading ? (
            <li style={{ display: "flex", alignItems: "center", gap: token.s2, padding: token.s2, color: token.slate }}>
              <Spinner /> {t("catalogue.picker.searching")}
            </li>
          ) : query.isError ? (
            <li role="alert" style={{ padding: token.s2, color: token.critical }}>
              {query.error?.message ?? t("catalogue.picker.error")}
            </li>
          ) : rows.length === 0 ? (
            <li style={{ padding: token.s2, color: token.slate }}>{t("catalogue.picker.empty")}</li>
          ) : (
            rows.map((row, i) => {
              const key = getKey(row);
              const isActive = i === active;
              return (
                <li
                  key={key}
                  role="option"
                  aria-selected={isActive}
                  // mousedown (not click) so it fires before the input blur closes the list.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (blurTimer.current) clearTimeout(blurTimer.current);
                    pick(row);
                  }}
                  onMouseEnter={() => setActive(i)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: token.s2,
                    padding: token.s2,
                    borderRadius: token.rSm,
                    cursor: "pointer",
                    background: isActive ? token.surface2 : "transparent",
                  }}
                >
                  {renderItem(row)}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
